import * as React from 'react';
import type { AnyStyledComponent, DefaultTheme } from 'styled-components';
import styled from 'styled-components';
import type { HeadlineProps } from '@oneaudi/unified-web-components';
import type { Logger } from '@feature-hub/logger';
import {
  Accordion as AudiAccordion,
  AccordionSection as AudiAccordionSection,
  Headline,
} from '@oneaudi/unified-web-components';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import type { OnegraphDataType } from '@oneaudi/feature-app-utils';
import { useContent, useConsumptionsAndEmissionsFromOneGraph } from '@oneaudi/feature-app-utils';
import { renderTextWithFootnotesReferences } from '../../feature-service-types';
import { UeContainer, UeElement, UeReference } from '@oneaudi/falcon-tools';
import { useState } from 'react';
import type { AccordionContent, FeatureAppMeta } from '../../../types';

import { isDebugMode } from '../../utils/utils';
import { APP_ID } from '../../../environment';
import { SideBySide } from './SideBySide';
import { RichText } from './RichText';
import LegalData from './LegalData';
import CTAs from './CTAs';
import { StructuredData } from './StructuredData';
import type { FalconContent } from '../../contentMapping/contentMapping';
import { mapContent } from '../../contentMapping/contentMapping';
import type { TrackingClickEvent } from '../../tracking';
import { useTracking } from '../../tracking';
import { Context } from '../../Context';
import type { VideoPlayerHandle } from './Video';
import { getThemeName } from '../utils/alpha-helpers';
import {
  SYS_COLOR_FOREGROUND_INTERACTIVE_ON_CANVAS_200_DARK,
  SYS_TYPOGRAPHY_HEADLINE_ORDER4_NORMAL,
} from '@oneaudi/unified-web-alpha-common-v2';

const StyledContainer = styled.div`
  font-family: ${SYS_TYPOGRAPHY_HEADLINE_ORDER4_NORMAL['2xl'].fontFamily};
  font-stretch: ${SYS_TYPOGRAPHY_HEADLINE_ORDER4_NORMAL['2xl'].fontStretch};
  line-height: ${SYS_TYPOGRAPHY_HEADLINE_ORDER4_NORMAL['2xl'].lineHeight};
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    process.env.IS_LEGACY ? theme.global.color.canvas.default : ''};
  color: ${({ theme }: { theme: DefaultTheme }) =>
    process.env.IS_LEGACY
      ? theme.sys.color.foreground.interactive.primary[100]
      : SYS_COLOR_FOREGROUND_INTERACTIVE_ON_CANVAS_200_DARK};
  padding-inline: var(--page-margin);
  padding-block-start: var(--spacing-relative-xl);
  padding-block-end: var(--spacing-relative-xl);
  .cta {
    padding: var(--spacing-relative-xl) var(--spacing-relative-xl);
  }
  .audi-footnote-anchor__text {
    vertical-align: super !important;
  }
  .audi-j-footnote-reference {
    color: inherit !important;
    font-weight: normal !important;
    padding: 0 !important;
    text-decoration: none !important;
    border-bottom: 0 !important;
  }
`;

const StyledHeadline = styled(Headline)<HeadlineProps>`
  && {
    margin-bottom: var(--spacing-relative-xl);
  }
`;

const StyledAccordion = styled(AudiAccordion as AnyStyledComponent)`
  > div {
    white-space: normal;
  }
`;

/**
 * Styled wrapper for accordion section content in legacy mode.
 * Provides visual distinction between the label (question) and content (answer)
 * by adding a gray background color per Figma design specs.
 */
const SectionContent = styled.div`
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    process.env.IS_LEGACY ? theme.global.color.elevation.level4 : 'transparent'};
  padding: ${process.env.IS_LEGACY ? 'var(--spacing-relative-md)' : '0'};
`;

export const Accordion = ({
  themeOption,
  headlineOptions,
  accordionOptions,
  structuredDataOptions,
  sections,
  meta,
}: AccordionContent & {
  meta: FeatureAppMeta;
}): React.ReactElement => {
  const headlineTag = headlineOptions && headlineOptions.tag ? headlineOptions.tag : 'h2';
  const ref = React.useRef(null);

  const { localeService, oneGraphService, trackingService } = React.useContext(Context);
  const videoRefs = React.useRef<Array<VideoPlayerHandle | null>>([]);

  // preload cae data
  const ids = sections[0].consumptionAndEmissions;
  const options = React.useMemo(
    () => ({ ids, localeService, oneGraphService }),
    [ids, localeService, oneGraphService],
  );
  const [caeData, setCaeData] = React.useState<OnegraphDataType | undefined>(undefined);
  const data = useConsumptionsAndEmissionsFromOneGraph(options);

  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCaeData(data);
  }, [data]);

  // check if accordion 1st section is by default open
  // if so - check if preloaded data is still loading
  const isDefaultOpen = accordionOptions && accordionOptions.isAutoOpen;
  const [openSections, setOpenSections] = useState(() => {
    return sections.map((_, index) => {
      return isDefaultOpen && index === 0;
    });
  });
  const firstSectionHasCaE =
    Boolean(localeService) &&
    Boolean(oneGraphService) &&
    sections.length > 0 &&
    sections[0].consumptionAndEmissions?.length > 0;
  const isWaitingForCaE = firstSectionHasCaE && isDefaultOpen && !caeData;

  const { sendReadyEvent, registerImpressionTracking, sendClickEvent } =
    useTracking(trackingService);

  if (typeof window !== 'undefined') {
    // skip tracking on the server side where window object is undefined

    React.useEffect(() => {
      sendReadyEvent();
    }, []);

    React.useEffect(() => {
      if (ref?.current)
        registerImpressionTracking(ref, {
          value: headlineOptions.headline || '',
        });
    }, [ref]);
  }
  const isSectionOpen = (index: number) => openSections[index];

  const toggleSection = (index: number, currentSectionLabel: string) => {
    setOpenSections((prevState) => {
      const isCurrentlyOpen = prevState[index];

      if (accordionOptions && accordionOptions.hasMultipleItemsAllowed) {
        if (isCurrentlyOpen) {
          videoRefs.current[index]?.pause();
        }
        // Toggle just the clicked section
        return prevState.map((isOpen, i) => (i === index ? !isOpen : isOpen));
      } else {
        // Allow only one open: toggle the clicked section, close all others
        const newState = prevState.map((_, i) => (i === index ? !prevState[index] : false));

        // Pause/reset any section that was open and is now being closed
        prevState.forEach((wasOpen, i) => {
          const willBeOpen = newState[i];
          if (wasOpen && !willBeOpen) {
            videoRefs.current[i]?.pause(); // or .reset()
          }
        });

        return newState;
      }
    });
    sendAccordionClickEvent(isSectionOpen(index), currentSectionLabel);
  };
  const sendAccordionClickEvent = (open: boolean, label: string) => {
    const event: TrackingClickEvent = {
      linkLabel: label,
      linkUrl: '',
      elementName: 'other',
      headline: headlineOptions.headline || '',
      clickEventName: `${open ? 'close' : 'open'} accordion`,
      sectionAction: 'content',
    };
    sendClickEvent(event);
  };

  return (
    <div ref={ref} data-testid="accordion">
      <ThemeProvider theme={{ name: getThemeName(themeOption?.theme) }}>
        <StyledContainer>
          {headlineOptions.headline && (
            <StyledHeadline
              variant={{
                order: '2',
                type: 'headline',
                weight: 'normal',
              }}
              tag={headlineTag}
            >
              <UeElement type="text" property="headlineOptions_headline" label="Headline">
                {renderTextWithFootnotesReferences(headlineOptions.headline || '')}
              </UeElement>
            </StyledHeadline>
          )}

          <UeContainer propertyPath="sections" label="Sections">
            <StyledAccordion data-testid="audi-accordion">
              {sections.map((section, sectionIndex) => {
                return (
                  <AudiAccordionSection
                    key={section.label}
                    label={
                      <UeReference propertyPath={`sections.${sectionIndex}`} as="div">
                        <UeElement type="text" property="label" label="Label">
                          {section.label}
                        </UeElement>
                      </UeReference>
                    }
                    open={openSections[sectionIndex]}
                    onToggle={() => {
                      toggleSection(sectionIndex, section.label);
                    }}
                  >
                    <SectionContent>
                      <UeReference propertyPath={`sections.${sectionIndex}`} as="span">
                        {section?.asset?.assetType && section?.asset?.assetType !== 'none' ? (
                          <SideBySide
                            ref={(el: VideoPlayerHandle | null) =>
                              (videoRefs.current[sectionIndex] = el)
                            }
                            asset={section?.asset}
                            sectionIndex={sectionIndex}
                            meta={meta}
                          >
                            <UeElement type="text" property="body" label="Copy">
                              <RichText text={section.body} />
                            </UeElement>
                          </SideBySide>
                        ) : (
                          <UeElement type="text" property="body" label="Copy">
                            <RichText text={section.body} />
                          </UeElement>
                        )}
                      </UeReference>
                      <UeContainer propertyPath={`sections.${sectionIndex}`}>
                        <UeReference
                          propertyPath={`sections.${sectionIndex}`}
                          as="div"
                          label="CTAs"
                        >
                          {section.links && section.links.length > 0 ? (
                            <CTAs links={section.links} headline={headlineOptions.headline || ''} />
                          ) : null}
                        </UeReference>
                      </UeContainer>
                      {/* if by default 1st section is open and data is still loading ->
                      do not show LegalData component */}
                      {!isWaitingForCaE && (
                        <LegalData
                          ctas={Boolean(section.links)}
                          consumptionsAndEmissions={section.consumptionAndEmissions}
                          disclaimers={section.disclaimers}
                        />
                      )}
                    </SectionContent>
                  </AudiAccordionSection>
                );
              })}
            </StyledAccordion>
          </UeContainer>
        </StyledContainer>
      </ThemeProvider>
      {structuredDataOptions?.enableStructuredData && (
        <StructuredData sections={sections} headline={headlineOptions.headline} />
      )}
    </div>
  );
};

export const AccordionLoader = ({
  meta,
  initialContent,
  loggerService,
}: {
  meta: FeatureAppMeta;
  initialContent?: AccordionContent;
  loggerService?: Logger;
}): React.ReactElement | null => {
  const rawContent = useContent<AccordionContent | FalconContent>() || initialContent;
  const content = mapContent(rawContent as AccordionContent | FalconContent, loggerService);

  if (isDebugMode()) loggerService?.debug(`${APP_ID} ->`, { content });
  if (!content) return null;
  return <Accordion {...content} meta={meta} />;
};
